import { CloudUpload, RotateCw, X } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { formatBytes, type UploadItem } from '@/lib/upload';
import { cn } from '@/lib/utils';
import { Button } from './button';

/** Área de soltar arquivos. Também abre o seletor ao clicar ou com Enter/Espaço. */
export function Dropzone({
  onFiles,
  accept,
  multiple = true,
  disabled,
  children,
  className,
}: {
  onFiles: (files: FileList) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  className?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={() => !disabled && input.current?.click()}
      onKeyDown={(e) =>
        (e.key === 'Enter' || e.key === ' ') && !disabled && (e.preventDefault(), input.current?.click())
      }
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (!disabled && e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
      }}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-panel px-6 py-8 text-center text-[13.5px] text-fg-muted transition-colors',
        'border-[1.5px] border-dashed border-line-strong hover:bg-hover',
        over && 'border-signal bg-signal-soft text-fg',
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
    >
      <CloudUpload className={cn('size-5', over ? 'text-signal' : 'text-fg-subtle')} />
      {children ?? (
        <span>
          Arraste arquivos aqui ou{' '}
          <span className="text-fg underline underline-offset-4">escolha no computador</span>
        </span>
      )}
      <input
        ref={input}
        type="file"
        hidden
        multiple={multiple}
        accept={accept}
        onChange={(e) => {
          if (e.target.files?.length) onFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}

const STATUS_LABEL: Record<UploadItem['status'], string> = {
  preparing: 'Preparando',
  uploading: 'Enviando',
  finishing: 'Finalizando',
  done: 'Enviado',
  error: 'Falhou',
  canceled: 'Cancelado',
};

/** Lista de envios em andamento: progresso, cancelar, tentar de novo. */
export function UploadList({
  items,
  onCancel,
  onRetry,
  onDismiss,
}: {
  items: UploadItem[];
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  onDismiss: (id: string) => void;
}) {
  if (!items.length) return null;
  return (
    <ul className="flex flex-col gap-1.5" aria-live="polite">
      {items.map((it) => {
        const active = it.status === 'preparing' || it.status === 'uploading' || it.status === 'finishing';
        return (
          <li
            key={it.id}
            className="relative overflow-hidden rounded-control bg-raised px-3 py-2.5 shadow-[inset_0_0_0_1px_var(--color-line)]"
          >
            {active && (
              <div
                className="absolute inset-y-0 left-0 bg-signal-soft transition-[width] duration-200"
                style={{ width: `${Math.round(it.progress * 100)}%` }}
              />
            )}
            <div className="relative flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13.5px]">{it.file.name}</p>
                <p
                  className={cn(
                    'tabular text-[12px]',
                    it.status === 'error' ? 'text-danger' : 'text-fg-muted',
                  )}
                >
                  {it.status === 'error'
                    ? it.error
                    : `${STATUS_LABEL[it.status]}${active ? ` ${Math.round(it.progress * 100)}%` : ''}`}
                  <span className="ml-2 text-fg-subtle">{formatBytes(it.file.size)}</span>
                </p>
              </div>
              {active && (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label={`Cancelar envio de ${it.file.name}`}
                  onClick={() => onCancel(it.id)}
                >
                  <X />
                </Button>
              )}
              {(it.status === 'error' || it.status === 'canceled') && (
                <Button size="sm" variant="ghost" onClick={() => onRetry(it.id)}>
                  <RotateCw /> Tentar de novo
                </Button>
              )}
              {!active && (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Remover da lista"
                  onClick={() => onDismiss(it.id)}
                >
                  <X />
                </Button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
